// src/services/api.ts
import axios, {
  AxiosInstance,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { FooterData } from "../pages/Dashboard/footerContent";

// Define interfaces for your data structures
interface Booking {
  id?: string;
  name?: string;
  // Add other booking properties
}

interface OurStory {
  id: string;
  content: string;
  // Add other story properties
}

interface GalleryEntry {
  id: string;
  imageUrl: string;
  // Add other gallery properties
}

interface Product {
  id: string;
  name: string;
  price: number;
  // Add other product properties
}

interface Review {
  id: string;
  clientName: string;
  comment: string;
  video: string;
  // Add other review properties
}

interface CalendarDay {
  id: string;
  date: string;
  // Add other calendar properties
}

interface AuthData {
  email_or_username: string;
  password: string;
  // Add other auth properties
}

interface GlamPackage {
  id: string;
  name: string;
  description: string;
  price: number;
  // Add other glam package properties
}

interface Category {
  id?: string;
  name: string;
  description: string;
  // Add other category properties
}

// Public API instance (for website)
export const publicApi: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
});

// Protected API instance (for dashboard)
export const protectedApi: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
});

protectedApi.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const user = localStorage.getItem("user");
    const token = user ? JSON.parse(user).token : null;

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Bookings
export const createBooking = (
  bookingData: Booking
): Promise<AxiosResponse<Booking>> =>
  publicApi.post<Booking>("/bookings", bookingData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
export const getAllBookings = (): Promise<AxiosResponse<Booking[]>> =>
  protectedApi.get<Booking[]>("/bookings");
export const getBooking = (id: string): Promise<AxiosResponse<Booking>> =>
  protectedApi.get<Booking>(`/bookings/${id}`);
export const setBookingStatus = (
  id: string,
  status: string
): Promise<AxiosResponse<Booking>> =>
  protectedApi.post<Booking>(`/bookings/${id}/status`, { status });
export const deleteBooking = (id: string): Promise<AxiosResponse<void>> =>
  protectedApi.delete<void>(`/bookings/${id}`);
export const updateBooking = (
  id: string,
  bookingData: Booking
): Promise<AxiosResponse<void>> =>
  protectedApi.post<void>(`/bookings/update/${id}`, bookingData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

// Quote Requests API
export const getQuoteRequests = async () => {
  return await protectedApi.get("/quote-bookings");
};

export const getQuoteRequest = async (id: string) => {
  return await protectedApi.get(`/quote-bookings/${id}`);
};

export const createQuoteRequest = async (data: any) => {
  return await publicApi.post("/quote-bookings", data, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

export const updateQuoteRequest = async (id: string, data: any) => {
  return await protectedApi.post(`/quote-bookings/update/${id}`, data, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};

export const deleteQuoteRequest = async (id: string) => {
  return await protectedApi.delete(`/quote-bookings/${id}`);
};

export const convertQuoteToBooking = async (id: string) => {
  return await protectedApi.post(`/quote-bookings/${id}/convert`);
};

export const getDuration = async () => {
  return await publicApi.get("/bookings/duration");
};

export const updateDuration = async (id: string, data: any) => {
  return await protectedApi.patch(`/bookings/duration/${id}`, data);
};

export const createDurationForPackage = async (data: {
  hours: number;
  minutes: number;
  glam_package_id: any;
}) => {
  return await protectedApi.post(`/glam-durations`, data);
};

export const getDurationForPackage = async (packageId: string) => {
  return await publicApi.get(`/glam-durations/${packageId}`);
};

export const updateDurationForPackage = async (id: any, data: any) => {
  return await protectedApi.patch(`/glam-durations/${id}`, data);
};

// Our Story
export const createOurStory = (
  formData: FormData
): Promise<AxiosResponse<OurStory>> => {
  return protectedApi.post<OurStory>("/our-story", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};
export const getOurStory = (id: string): Promise<AxiosResponse<OurStory>> =>
  protectedApi.get<OurStory>(`/our-story/${id}`);
export const getAllOurStories = (): Promise<AxiosResponse<OurStory>> =>
  publicApi.get<OurStory>("/our-story");
export const updateOurStory = (
  id: string,
  storyData: OurStory
): Promise<AxiosResponse<OurStory>> =>
  protectedApi.post<OurStory>(`/our-story/update/${id}`, storyData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
export const deleteOurStory = (id: string): Promise<AxiosResponse<void>> =>
  protectedApi.delete<void>(`/our-story/${id}`);

// Gallery
export const addGalleryEntry = (formData: FormData): Promise<AxiosResponse<{ message: string }>> =>
  protectedApi.post<{ message: string }>("/media/upload", formData, {
    headers: {
      "Content-Type": "multipart/form-data"
    }
  });
export const getAllGalleryEntries = (): Promise<AxiosResponse<GalleryEntry[]>> =>
  publicApi.get<GalleryEntry[]>("/instagram");
export const deleteGalleryImages = (ids: string[]): Promise<AxiosResponse<void>> =>
  protectedApi.post<void>("/media/delete", { ids });

// Products
export const createProduct = (
  productData: Product
): Promise<AxiosResponse<Product>> =>
  protectedApi.post<Product>("/products", productData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
export const updateProduct = (
  id: string,
  productData: Product
): Promise<AxiosResponse<Product>> =>
  protectedApi.post<Product>(`/products/update/${id}`, productData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
export const getProduct = (id: string): Promise<AxiosResponse<Product>> =>
  publicApi.get<Product>(`/products/${id}`);
export const deleteProduct = (id: string): Promise<AxiosResponse<void>> =>
  protectedApi.delete<void>(`/products/${id}`);
export const changeProductStatus = (
  id: string,
  status: string
): Promise<AxiosResponse<Product>> =>
  protectedApi.patch<Product>(`/products/${id}/status`, { status });
export const deleteProductImage = (id: string): Promise<AxiosResponse<void>> =>
  protectedApi.post<void>(`/products/${id}/delete-image`);
export const getAllProducts = (): Promise<AxiosResponse<Product[]>> =>
  publicApi.get<Product[]>("/products");

// Calendar
export const getCalendarDays = (
  calendarData: any
): Promise<AxiosResponse<CalendarDay[]>> =>
  publicApi.post<CalendarDay[]>("/calendar/days", calendarData);
export const checkAvailability = (
  availabilityData: any
): Promise<AxiosResponse<boolean>> =>
  publicApi.post<boolean>("/calendar/availability", availabilityData);

// Returns only genuinely bookable start slots for a date (server applies working
// hours, lead time, buffer, closures and Google Calendar busy times).
export const getDaySlots = (
  payload: {
    date: string;
    glam_package_id: string;
    number_of_faces?: number;
  },
  options?: { signal?: AbortSignal }
) => publicApi.post("/calendar/slots", payload, { signal: options?.signal });

// Availability rules (working hours, buffer, lead time, closures)
export const getBookingSettings = () => publicApi.get("/booking-settings");
export const updateBookingSettings = (data: any) =>
  protectedApi.patch("/booking-settings", data);

// Reviews
export const addReview = (
  formData: FormData
): Promise<AxiosResponse<Review>> => {
  return protectedApi.post<Review>("/reviews", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};
export const getReview = (id: string): Promise<AxiosResponse<Review>> =>
  publicApi.get<Review>(`/reviews/${id}`);
export const getAllReviews = (): Promise<AxiosResponse<Review[]>> =>
  publicApi.get<Review[]>("/reviews");
export const updateReview = (
  id: string,
  formData: FormData
): Promise<AxiosResponse<Review>> =>
  protectedApi.post<Review>(`/reviews/update/${id}`, formData);
export const deleteReview = (id: string): Promise<AxiosResponse<void>> =>
  protectedApi.delete<void>(`/reviews/${id}`);

// Auth
export const signUp = (authData: AuthData): Promise<AxiosResponse<void>> =>
  protectedApi.post<void>("/auth/signup", authData);
export const login = (credentials: AuthData): Promise<{ data: any }> =>
  protectedApi.post<void>("/auth/login", credentials);
export const adminLogout = (): Promise<AxiosResponse<void>> => {
  const user = localStorage.getItem("user"); // Retrieve user object
  const token = user ? JSON.parse(user).token : null; // Extract token from user object

  return protectedApi.get<void>("/auth/logout", {
    headers: {
      Authorization: token ? `Bearer ${token}` : "", // Attach token if available
    },
  });
};

// Glam Packages
export const createGlamPackage = (
  formData: FormData
): Promise<AxiosResponse<GlamPackage>> => {
  return protectedApi.post<GlamPackage>("/glam-packages", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
};
export const getGlamPackage = (
  id: string
): Promise<AxiosResponse<GlamPackage>> =>
  publicApi.get<GlamPackage>(`/glam-packages/${id}`);
export const getAllGlamPackages = (): Promise<AxiosResponse<GlamPackage[]>> =>
  publicApi.get<GlamPackage[]>("/glam-packages");
export const updateGlamPackage = (
  id: string,
  formData: FormData
): Promise<AxiosResponse<GlamPackage>> => {
  return protectedApi.post<GlamPackage>(
    `/glam-packages/update/${id}`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );
};
export const deleteGlamPackage = (id: string): Promise<AxiosResponse<void>> =>
  protectedApi.delete<void>(`/glam-packages/${id}`);

// Product Categories
export const createCategory = (
  categoryData: Category
): Promise<AxiosResponse<Category>> =>
  protectedApi.post<Category>("/categories", categoryData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });
export const getAllCategories = (): Promise<AxiosResponse<Category[]>> =>
  publicApi.get<Category[]>("/categories");
export const updateCategory = (
  id: string,
  categoryData: Category
): Promise<AxiosResponse<Category>> =>
  protectedApi.patch<Category>(`/categories/${id}`, categoryData);
export const deleteCategory = (id: string): Promise<AxiosResponse<void>> =>
  protectedApi.delete<void>(`/categories/${id}`);
export const getCategory = (id: string): Promise<AxiosResponse<Category>> =>
  publicApi.get<Category>(`/categories/${id}`);

// protectedApi.ts
export const getDashboardStats = () => protectedApi.get(`/analytics/stats`);

export const getFooter = async () => {
  return await publicApi.get("/footer");
};

export const createFooter = async (data: FooterData) => {
  return await protectedApi.post("/footer", data);
};

export const updateFooter = async (id: string, data: FooterData) => {
  return await protectedApi.patch(`/footer/update/${id}`, data);
};

export const getPrivacyPolicy = async () => {
  return await publicApi.get("/privacy-policy");
};

export const updatePrivacyPolicy = async (id: string, data: any) => {
  return await protectedApi.patch(`/privacy-policy/${id}`, data);
};

export const createBanner = async () => {
  return await publicApi.get("/privacy-policy");
};

export const deleteBanner = async (id: string) => {
  return await protectedApi.delete(`/privacy-policy/${id}`);
};

export const getBanners = async () => {
  return await publicApi.get("/privacy-policy");
};

export const updateBanner = async (id: string, data: any) => {
  return await protectedApi.patch(`/privacy-policy/${id}`, data);
};

export const getInstagramMedia = async (params?: { after?: any }) => {
  return await publicApi.get("/instagram", { params });
};
export default protectedApi;
